import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { getAssetDataSequenceSectionsQuery } from '@app/pages/cmms/components/assets/asset-time-series/misc/asset-time-series-queries';
import { catchError, map, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
	IAssetTimeSeries,
	TimeSeriesWithoutPagination,
} from '@app/pages/cmms/components/assets/asset-time-series/misc/interfaces';
import {
	createAssetTimeseries,
	createOrEditChartSection,
	deleteAssetTimeseries,
	deleteChartSection,
	editAssetTimeseries,
} from '@app/pages/cmms/components/assets/asset-time-series/misc/asset-timeseries-mutations';

@Injectable()
export class AssetTimeSeriesService {
	private readonly apollo: Apollo = inject(Apollo);

	timeSeries$ = new BehaviorSubject<TimeSeriesWithoutPagination[]>(null);

	updateStoredTimeSeriesData(data: TimeSeriesWithoutPagination[]) {
		this.timeSeries$.next(structuredClone(data || []));
	}

	private invalidateCache(assetId: string) {
		this.apollo.client.cache.evict({
			id: 'ROOT_QUERY',
			fieldName: `asset({"id":"${assetId}"})`,
		});
		this.apollo.client.cache.gc();
	}

	fetchAndStoreTimeSeries(assetId: string) {
		this.getAssetTimeSeriesSectionsData(assetId)
			.pipe(
				take(1),
				catchError(err => {
					console.error(err);
					return of([]);
				})
			)
			.subscribe(timeSeries => {
				this.updateStoredTimeSeriesData(timeSeries);
			});
	}

	getAssetTimeSeriesSectionsData(
		assetId: string
	): Observable<TimeSeriesWithoutPagination[]> {
		this.invalidateCache(assetId);
		return this.apollo
			.query({
				query: getAssetDataSequenceSectionsQuery,
				variables: {
					assetId,
				},
			})
			.pipe(
				map(({ data }) => {
					return data.asset.timeSeriesCharts.edges.map(({ node }) => {
						return {
							...node,
							timeSeries: node.timeSeries.edges.map(edge => edge.node),
						};
					});
				})
			);
	}

	createAssetTimeSeriesData(chartId: string, value: number, datetime: Date) {
		return this.apollo
			.mutate<{
				createAssetTimeseries: {
					timeseries: IAssetTimeSeries[];
				};
			}>({
				mutation: createAssetTimeseries,
				variables: {
					timeSeries: [
						{
							chartId,
							value,
							datetime,
						},
					],
				},
			})
			.pipe(map(({ data }) => data.createAssetTimeseries.timeseries));
	}

	editAssetTimeSeries(
		chartId: string,
		name?: string,
		description?: string,
		settings?: Partial<{
			benchmark: number;
			lowerBound: number;
			unit: string;
			upperBound: number;
		}>
	) {
		return this.apollo
			.mutate({
				mutation: createOrEditChartSection,
				variables: {
					chartId,
					name,
					description,
					...(settings || {}),
				},
			})
			.pipe(map(({ data }) => data.createOrUpdateAssetTimeseriesChart.settings));
	}

	deleteAssetTimeSeriesData(pk: string) {
		return this.apollo.mutate({
			mutation: deleteAssetTimeseries,
			variables: {
				ids: [pk],
			},
		});
	}

	updateAssetTimeSeriesData(
		pk: string,
		value: number,
		date?: Date
	): Observable<IAssetTimeSeries> {
		return this.apollo
			.mutate<{
				editAssetTimeseries: {
					timeseries: IAssetTimeSeries;
				};
			}>({
				mutation: editAssetTimeseries,
				variables: {
					date,
					value,
					pk,
				},
			})
			.pipe(map(({ data }) => data.editAssetTimeseries.timeseries));
	}

	createChartSection(
		assetId: string,
		chartName: string,
		description?: string,
		unit?: string
	) {
		return this.apollo
			.mutate({
				mutation: createOrEditChartSection,
				variables: {
					assetId,
					name: chartName,
					description,
					unit,
				},
			})
			.pipe(map(({ data }) => data.createOrUpdateAssetTimeseriesChart));
	}

	deleteChartSection(chartId: string) {
		return this.apollo.mutate({
			mutation: deleteChartSection,
			variables: {
				chartId: chartId,
			},
		});
	}
}
